/**
 * Hook for transforming query result data into chart-ready format.
 */

import { useMemo } from 'react';
import type { ChartConfig, TransformedChartData } from '../types/chartTypes';
import type { QueryResultSet } from '../../../../types/queryExecution';
import {
  transformChartData,
  transformPieChartData,
  transformScatterChartData,
  sampleData,
  MAX_CHART_DATA_POINTS,
} from '../utils/chartDataShaping';
import { analyzeColumns } from '../utils/columnTypeDetection';
import { resolveChartConfigColumnRefs } from '../utils/chartColumnRefs';
import type { ColumnTypeInfo } from '../types/chartTypes';

type UseChartDataResult = {
  /** Transformed chart data ready for Recharts */
  chartData: TransformedChartData;
  /** Whether data was sampled due to size */
  wasSampled: boolean;
  /** Original row count before sampling */
  originalRowCount: number;
  /** Column type information */
  columnInfos: ColumnTypeInfo[];
  /** Whether data is available for charting */
  hasData: boolean;
  /**
   * `config` with every column reference resolved to a row KEY (row KEY,
   * unique NAME, or — for an older ipynb/dashboard config — a blank/
   * whitespace NAME rekeyed to `col_<i>`) against the current result set's
   * columns. `chartData` was shaped from this, not the raw `config` — pass
   * IT (not `config`) to `ChartRenderer`/mark-event resolution, or a stale
   * name-based reference shapes correctly but mismatches the `field`/
   * `seriesField` a mark click resolves (P2 finding companion). Same object
   * identity as `config` when nothing needed resolving. `undefined` iff
   * `config` is.
   */
  resolvedConfig: ChartConfig | undefined;
};

/**
 * Transforms a QueryResultSet into chart-ready data based on ChartConfig.
 * Handles sampling for large datasets and type-specific transformations.
 */
export function useChartData(
  resultSet: QueryResultSet | undefined,
  config: ChartConfig | undefined
): UseChartDataResult {
  const columnInfos = useMemo(() => {
    if (!resultSet?.columns || !resultSet?.rows) return [];
    return analyzeColumns(resultSet.columns, resultSet.rows);
  }, [resultSet?.columns, resultSet?.rows]);

  // Resolve persisted column references against the current result set's
  // columns before shaping, so a saved config keeps grouping/plotting the
  // right column even after duplicate-column dedup renamed its key (P2
  // finding). Computed independently of the rows-empty branches below so
  // `resolvedConfig` is still exposed (and still correct) when there's no
  // data to shape yet.
  const resolvedConfig = useMemo(
    () => (config ? resolveChartConfigColumnRefs(config, columnInfos) : undefined),
    [config, columnInfos]
  );

  const result = useMemo((): UseChartDataResult => {
    const emptyResult: UseChartDataResult = {
      chartData: { data: [], seriesKeys: [], xDataKey: 'x', xIsNumeric: false },
      wasSampled: false,
      originalRowCount: 0,
      columnInfos,
      hasData: false,
      resolvedConfig,
    };

    if (!resultSet?.rows || !resolvedConfig) {
      return emptyResult;
    }

    const rows = resultSet.rows;
    const originalRowCount = rows.length;

    if (originalRowCount === 0) {
      return emptyResult;
    }

    // Sample data if too large
    const { sampled, wasSampled } = sampleData(rows, MAX_CHART_DATA_POINTS);

    // Look up the X column's detected category
    const xColInfo = resolvedConfig.xAxis?.column
      ? columnInfos.find((c) => c.key === resolvedConfig.xAxis.column)
      : undefined;
    const xColumnCategory = xColInfo?.category ?? 'unknown';

    // Transform based on chart type
    let chartData: TransformedChartData;

    switch (resolvedConfig.type) {
      case 'pie':
        chartData = transformPieChartData(sampled, resolvedConfig, xColumnCategory);
        break;
      case 'scatter':
        chartData = transformScatterChartData(sampled, resolvedConfig);
        break;
      default:
        chartData = transformChartData(sampled, resolvedConfig, xColumnCategory);
    }

    return {
      chartData,
      wasSampled,
      originalRowCount,
      columnInfos,
      hasData: chartData.data.length > 0,
      resolvedConfig,
    };
  }, [resultSet?.rows, resolvedConfig, columnInfos]);

  return result;
}

/**
 * Gets suggested column assignments based on column types.
 * Returns suggested X-axis, Y-axis columns, and optional series column.
 */
export function useSuggestedColumns(columnInfos: ColumnTypeInfo[]): {
  suggestedXColumn: string | null;
  suggestedYColumns: string[];
  suggestedSeriesColumn: string | null;
} {
  return useMemo(() => {
    const numericCols = columnInfos.filter((c) => c.category === 'numeric');
    const dateCols = columnInfos.filter((c) => c.category === 'date');
    const stringCols = columnInfos.filter((c) => c.category === 'string');

    let suggestedXColumn: string | null = null;
    let suggestedYColumns: string[] = [];
    let suggestedSeriesColumn: string | null = null;

    // Prefer date for X-axis if available
    if (dateCols.length > 0) {
      suggestedXColumn = dateCols[0].key;
    } else if (stringCols.length > 0) {
      // Use first string column for X-axis
      suggestedXColumn = stringCols[0].key;
      // Second string column could be series
      if (stringCols.length > 1) {
        suggestedSeriesColumn = stringCols[1].key;
      }
    } else if (numericCols.length >= 2) {
      // For scatter-like charts, first numeric as X
      suggestedXColumn = numericCols[0].key;
    }

    // Use numeric columns for Y-axis
    if (numericCols.length > 0) {
      // If X is numeric (for scatter), skip it for Y
      if (suggestedXColumn && numericCols[0].key === suggestedXColumn) {
        suggestedYColumns = numericCols.slice(1).map((c) => c.key);
      } else {
        suggestedYColumns = numericCols.map((c) => c.key);
      }
    }

    return {
      suggestedXColumn,
      suggestedYColumns,
      suggestedSeriesColumn,
    };
  }, [columnInfos]);
}
